import { ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { queryKeys } from '@/plugins/store/actions/queries/active-groups';
import { IconSource, MultiStepFormState } from '@/types';
import type { SectionAlarm } from '@/types/SectionLiveKpi';
import type { SectionRow } from '@/views/dashboard/monitoring/active-groups/sectionRow';

/**
 * Encapsulates all alarm-modal state and orchestration for the Active groups page.
 * Extracted from Index.vue so the state machine lives in one focused place.
 *
 * The ActionModal's `open` callback (from the scoped-slot pattern) is passed as a
 * parameter to `openAlarm` so this composable stays decoupled from the template slot.
 */
export function useAlarmModal() {
    const queryClient = useQueryClient();

    const alarmActionState = ref<MultiStepFormState>({
        location: null,
        form: null,
        confirmed: false,
        farm: null,
        record_id: null,
        notification: null,
        event: null,
    });

    const alarmStepConfig = ref([
        {},
        { deleteButton: true, editable: true },
        { disableRefetch: true, submitActionType: ActionType.PutAlarmForm },
        {},
    ]);

    const alarmActionHeader = ref<{ icon_url: IconSource | string; text: string }>({ icon_url: '', text: '' });

    const alarmListModalOpen = ref(false);
    const alarmListAlarms = ref<SectionAlarm[]>([]);
    const isLoadingAlarmForm = ref(false);

    async function loadAndOpenAlarm(alarmId: number, openFn: CallableFunction) {
        if (isLoadingAlarmForm.value) return;
        isLoadingAlarmForm.value = true;
        try {
            const alarmForm = await store.dispatch(ActionType.GetAlarmForm, { form: { alarm_id: alarmId } });
            if (alarmForm) {
                alarmStepConfig.value[1] = { deleteButton: alarmForm.deletable, editable: alarmForm.editable };
                alarmActionState.value = {
                    alarm_id: alarmId,
                    location: null,
                    form: alarmForm.form,
                    confirmed: false,
                    farm: null,
                    event: null,
                    record_id: null,
                    notification: null,
                };
                alarmActionHeader.value = { icon_url: IconSource.AlarmIcon, text: alarmForm.form.sections?.[0]?.name || '' };
                openFn();
            }
        } finally {
            isLoadingAlarmForm.value = false;
        }
    }

    function openAlarm(section: SectionRow, openFn: CallableFunction) {
        if (section.activeAlarms.length === 1) {
            loadAndOpenAlarm(section.activeAlarms[0].id, openFn);
        } else {
            alarmListAlarms.value = section.activeAlarms;
            alarmListModalOpen.value = true;
        }
    }

    function onAlarmFormClosing(wasFinalStep: boolean) {
        if (wasFinalStep) {
            queryClient.invalidateQueries({ queryKey: queryKeys.getSectionLiveKpisRoot });
        }
    }

    return {
        alarmActionState,
        alarmStepConfig,
        alarmActionHeader,
        alarmListModalOpen,
        alarmListAlarms,
        isLoadingAlarmForm,
        loadAndOpenAlarm,
        openAlarm,
        onAlarmFormClosing,
    };
}
